import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

// ── Warm palette ──────────────────────────────────────────────────────────────
const C = {
  terracota: '#C4866A',
  rose:      '#D4A5A0',
  gold:      '#C9A86C',
  cream:     '#FAF5EF',
  dark:      '#1C1410',
  muted:     'rgba(196,134,106,0.55)',
  bg:        'radial-gradient(ellipse at 50% 30%, #2A1810 0%, #1C1210 45%, #0E0A08 100%)',
};

const STEPS = [
  { id: 'stage',     label: 'Where are you right now?' },
  { id: 'child',     label: 'Is this your first child?' },
  { id: 'baby_name', label: 'What is her/his name?' },
  { id: 'support',   label: 'Do you have support at home?' },
  { id: 'intention', label: 'One thing you want to feel in this season' },
];

export default function MatrescenceOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    weeks_postpartum: null,   // null = pregnant, 0-52+ = weeks post
    first_child: null,
    baby_name: '',
    support_at_home: null,
    intention: '',
  });

  const isPregnant = data.weeks_postpartum === null || data.weeks_postpartum === 'pregnant';

  const set = (key, val) => setData(d => ({ ...d, [key]: val }));

  const canContinue = () => {
    const s = STEPS[step].id;
    if (s === 'stage')     return data.weeks_postpartum !== undefined;
    if (s === 'child')     return data.first_child !== null;
    if (s === 'baby_name') return data.baby_name.trim().length > 0;
    if (s === 'support')   return data.support_at_home !== null;
    if (s === 'intention') return data.intention.trim().length > 2;
    return true;
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
      return;
    }
    // Final step — save profile
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const profile = {
        weeks_postpartum: data.weeks_postpartum === 'pregnant' ? null : data.weeks_postpartum,
        first_child: data.first_child,
        baby_name: data.baby_name.trim(),
        support_at_home: data.support_at_home,
        intention: data.intention.trim(),
      };

      await axios.patch(`${API}/users/me/matrescence-profile`, profile, { headers });

      // Mark onboarding complete
      await axios.put(`${API}/users/${user.user_id}`, {
        has_completed_onboarding: true,
        mode: 'matrescence',
        matrescence_profile: profile,
      }, { headers });

      // Update local user cache
      const updated = { ...user, has_completed_onboarding: true, mode: 'matrescence', matrescence_profile: profile };
      localStorage.setItem('user', JSON.stringify(updated));

      navigate('/matrescence');
    } catch (err) {
      console.error('[Matrescence onboarding]', err);
      navigate('/matrescence');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step) / STEPS.length) * 100;

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px 48px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Soft ambient glow */}
      <div style={{
        position: 'absolute', top: '15%', left: '50%',
        transform: 'translateX(-50%)',
        width: 300, height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(196,134,106,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Progress bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: 'rgba(255,255,255,0.06)',
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${C.terracota}, ${C.rose})`,
          transition: 'width 0.5s ease',
          borderRadius: '0 2px 2px 0',
        }} />
      </div>

      {/* Step counter */}
      <p style={{
        position: 'absolute', top: 18, right: 24,
        fontSize: '0.72rem',
        color: 'rgba(255,255,255,0.22)',
        letterSpacing: '0.05em',
        margin: 0,
      }}>
        {step + 1} / {STEPS.length}
      </p>

      {/* Small lotus mark */}
      <div style={{ marginBottom: 36, opacity: 0.55 }}>
        <LotusIcon color={C.terracota} />
      </div>

      {/* Question label */}
      <p style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 'clamp(1.1rem, 4.5vw, 1.4rem)',
        color: 'rgba(255,255,255,0.85)',
        textAlign: 'center',
        margin: '0 0 32px',
        lineHeight: 1.5,
        fontStyle: 'italic',
        maxWidth: 340,
        animation: 'mat-fadein 0.45s ease forwards',
      }}>
        {STEPS[step].label}
      </p>

      {/* Step content */}
      <div style={{
        width: '100%', maxWidth: 380,
        animation: 'mat-fadein 0.45s ease forwards',
      }}>
        <StepContent
          stepId={STEPS[step].id}
          data={data}
          set={set}
          isPregnant={isPregnant}
        />
      </div>

      {/* CTA */}
      <button
        onClick={handleNext}
        disabled={!canContinue() || saving}
        style={{
          marginTop: 36,
          width: '100%',
          maxWidth: 380,
          padding: '16px',
          background: canContinue()
            ? `linear-gradient(135deg, ${C.terracota} 0%, ${C.rose} 100%)`
            : 'rgba(255,255,255,0.06)',
          border: 'none',
          borderRadius: 28,
          color: canContinue() ? '#fff' : 'rgba(255,255,255,0.25)',
          fontSize: '0.95rem',
          fontWeight: 600,
          cursor: canContinue() ? 'pointer' : 'default',
          letterSpacing: '0.02em',
          transition: 'all 0.3s ease',
          boxShadow: canContinue() ? `0 6px 28px rgba(196,134,106,0.35)` : 'none',
        }}
      >
        {saving ? 'Saving…' : step === STEPS.length - 1 ? 'Begin →' : 'Continue →'}
      </button>

      <style>{`
        @keyframes mat-fadein {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Step-specific content ─────────────────────────────────────────────────────
function StepContent({ stepId, data, set, isPregnant }) {
  if (stepId === 'stage') {
    const options = [
      { val: 'pregnant', label: 'I\'m still pregnant', sub: 'Preparing for this transition' },
      { val: 2,   label: 'Newborn (0–6 weeks)',  sub: 'The first tender weeks' },
      { val: 12,  label: '6 weeks – 3 months',   sub: 'Finding a new rhythm' },
      { val: 26,  label: '3–6 months',            sub: 'Emerging from the fog' },
      { val: 39,  label: '6–9 months',            sub: 'Deepening into motherhood' },
      { val: 52,  label: '9–12 months',           sub: 'First year integration' },
      { val: 78,  label: 'Over a year',           sub: 'Ongoing matrescence' },
    ];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {options.map(o => {
          const active = data.weeks_postpartum === o.val;
          return (
            <OptionCard
              key={o.val}
              active={active}
              label={o.label}
              sub={o.sub}
              onClick={() => set('weeks_postpartum', o.val)}
            />
          );
        })}
      </div>
    );
  }

  if (stepId === 'child') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <OptionCard active={data.first_child === true}  label="Yes, my first" onClick={() => set('first_child', true)} />
        <OptionCard active={data.first_child === false} label="I have other children too" onClick={() => set('first_child', false)} />
      </div>
    );
  }

  if (stepId === 'baby_name') {
    const placeholder = isPregnant ? 'If you have a name in mind…' : 'Baby\'s name';
    return (
      <div>
        <input
          type="text"
          value={data.baby_name}
          onChange={e => set('baby_name', e.target.value)}
          placeholder={placeholder}
          maxLength={40}
          style={{
            width: '100%',
            padding: '16px 18px',
            background: 'rgba(255,255,255,0.05)',
            border: '1.5px solid rgba(196,134,106,0.28)',
            borderRadius: 14,
            color: 'rgba(255,255,255,0.88)',
            fontSize: '1rem',
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            letterSpacing: '0.01em',
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(196,134,106,0.65)'}
          onBlur={e => e.target.style.borderColor = 'rgba(196,134,106,0.28)'}
        />
        {isPregnant && (
          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.25)',
            marginTop: 8, textAlign: 'center',
          }}>
            You can always add this later
          </p>
        )}
      </div>
    );
  }

  if (stepId === 'support') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <OptionCard active={data.support_at_home === true}  label="Yes, I have support" sub="Partner, family, or other support" onClick={() => set('support_at_home', true)} />
        <OptionCard active={data.support_at_home === false} label="Not as much as I'd like" sub="That's okay — HYPSEYA will help" onClick={() => set('support_at_home', false)} />
      </div>
    );
  }

  if (stepId === 'intention') {
    const suggestions = ['feel calm', 'reconnect with myself', 'be present', 'feel held', 'find my rhythm', 'trust myself'];
    return (
      <div>
        <textarea
          value={data.intention}
          onChange={e => set('intention', e.target.value)}
          placeholder="e.g. feel calm, trust myself, be present…"
          maxLength={120}
          rows={3}
          style={{
            width: '100%',
            padding: '16px 18px',
            background: 'rgba(255,255,255,0.05)',
            border: '1.5px solid rgba(196,134,106,0.28)',
            borderRadius: 14,
            color: 'rgba(255,255,255,0.88)',
            fontSize: '1rem',
            outline: 'none',
            resize: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            lineHeight: 1.55,
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(196,134,106,0.65)'}
          onBlur={e => e.target.style.borderColor = 'rgba(196,134,106,0.28)'}
        />
        {/* Suggestion chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
          {suggestions.map(s => (
            <button
              key={s}
              onClick={() => set('intention', s)}
              style={{
                padding: '6px 13px',
                background: data.intention === s ? 'rgba(196,134,106,0.22)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${data.intention === s ? 'rgba(196,134,106,0.5)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 20,
                color: data.intention === s ? '#C4866A' : 'rgba(255,255,255,0.42)',
                fontSize: '0.78rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                letterSpacing: '0.01em',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function OptionCard({ active, label, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        padding: sub ? '13px 16px' : '15px 16px',
        background: active ? 'rgba(196,134,106,0.15)' : 'rgba(255,255,255,0.04)',
        border: `1.5px solid ${active ? 'rgba(196,134,106,0.48)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 13,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left',
        transition: 'all 0.22s ease',
        outline: 'none',
      }}
    >
      <div>
        <p style={{
          margin: 0,
          color: active ? '#C4866A' : 'rgba(255,255,255,0.78)',
          fontSize: '0.92rem',
          fontWeight: active ? 600 : 400,
          transition: 'all 0.22s',
        }}>
          {label}
        </p>
        {sub && (
          <p style={{
            margin: '3px 0 0',
            color: 'rgba(255,255,255,0.28)',
            fontSize: '0.74rem',
          }}>
            {sub}
          </p>
        )}
      </div>
      <div style={{
        width: 17, height: 17,
        borderRadius: '50%',
        border: `1.5px solid ${active ? 'rgba(196,134,106,0.7)' : 'rgba(255,255,255,0.14)'}`,
        background: active ? 'rgba(196,134,106,0.3)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginLeft: 12,
        transition: 'all 0.22s',
      }}>
        {active && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#C4866A' }} />}
      </div>
    </button>
  );
}

function LotusIcon({ color = '#C4866A' }) {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
      {/* Center petal */}
      <path d="M18 28 C18 28 12 22 12 16 C12 11 15 8 18 8 C21 8 24 11 24 16 C24 22 18 28 18 28Z"
        stroke={color} strokeWidth="1.3" fill="none" opacity="0.8"/>
      {/* Left petal */}
      <path d="M18 28 C18 28 8 24 7 18 C6 13 9 10 12 11 C14 12 15 14 15 16"
        stroke={color} strokeWidth="1.1" fill="none" opacity="0.55"/>
      {/* Right petal */}
      <path d="M18 28 C18 28 28 24 29 18 C30 13 27 10 24 11 C22 12 21 14 21 16"
        stroke={color} strokeWidth="1.1" fill="none" opacity="0.55"/>
      {/* Stem */}
      <path d="M18 28 L18 33" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
      {/* Leaves */}
      <path d="M18 31 C16 30 13 30 12 32" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <path d="M18 31 C20 30 23 30 24 32" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    </svg>
  );
}
